import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { AddOutlined } from '@mui/icons-material';
import { Box, Button, Chip, Menu, MenuItem, Stack } from '@mui/material';
import { designTokens } from '../tokens';
import { SearchField } from './SearchField';
import { FormTextField } from './FormTextField';

export type FilterDefinition = {
  id?: string;
  label: string;
  value: string;
  options?: Array<{ label: string; value: string }>;
  kind?: 'options' | 'date';
  onChange: (value: string) => void;
  defaultValue?: string;
  active?: boolean;
  onReset?: () => void;
};

export type FilterBarSearchProps = {
  searchable?: true;
  query: string;
  onQueryChange: (value: string) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
} | {
  searchable: false;
  query?: never;
  onQueryChange?: never;
  searchPlaceholder?: never;
  searchLabel?: never;
};

export type CommonFilterBarProps = FilterBarSearchProps & {
  filters?: FilterDefinition[];
  /** 검색·필터 바로 옆에 배치하는 보기·탐색 도구입니다. */
  actions?: ReactNode;
  trailing?: ReactNode;
};

function filterID(filter: FilterDefinition) {
  return filter.id ?? filter.label;
}

function resetFilter(filter: FilterDefinition) {
  if (filter.onReset) filter.onReset();
  else filter.onChange(filter.defaultValue ?? '');
}

const controlChipSx = {
  height: designTokens.layout.pageHeaderControlSize,
  [designTokens.media.touchTarget]: { height: designTokens.layout.touchTargetMinSize },
  borderRadius: designTokens.layout.chipRadius,
  px: 1.5,
  gap: 1,
  ...designTokens.typography.ui,
  fontWeight: 500,
  '&.MuiChip-clickable:hover': { bgcolor: designTokens.color.hoverSurface },
  '&.MuiChip-clickable:active': { bgcolor: designTokens.color.selectedNavigationSurface, boxShadow: 'none' },
  '&.Mui-focusVisible': { bgcolor: designTokens.color.canvas },
  '@media (hover: none)': { '&.MuiChip-clickable:hover': { bgcolor: designTokens.color.canvas } },
  '& .MuiChip-label': { p: 0 },
  '& .MuiChip-icon': {
    width: designTokens.button.sizes.medium.iconSize,
    height: designTokens.button.sizes.medium.iconSize,
    fontSize: designTokens.button.sizes.medium.iconSize,
    m: 0,
    color: 'inherit',
  },
  '& .MuiChip-deleteIcon': {
    width: designTokens.button.sizes.medium.iconSize,
    height: designTokens.button.sizes.medium.iconSize,
    fontSize: designTokens.button.sizes.medium.iconSize,
    m: 0,
  },
} as const;

export function CommonFilterBar(props: CommonFilterBarProps) {
  const { filters = [], actions, trailing } = props;
  const externallyActiveFilterIDs = filters
    .filter((filter) => filter.active ?? filter.value !== (filter.defaultValue ?? ''))
    .map(filterID);
  const activeFilterSignature = externallyActiveFilterIDs.join('\u0000');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<HTMLElement>();
  const [filterMenuStage, setFilterMenuStage] = useState<'filter' | 'value'>('filter');
  const [editingFilterID, setEditingFilterID] = useState<string>();
  const [visibleFilterIDs, setVisibleFilterIDs] = useState<Set<string>>(() => new Set(externallyActiveFilterIDs));
  const filterChipAnchors = useRef(new Map<string, HTMLElement>());
  const filterTriggerRef = useRef<HTMLDivElement>(null);
  const restoreFilterFocus = useRef(false);

  useEffect(() => {
    if (!restoreFilterFocus.current) return;
    restoreFilterFocus.current = false;
    filterTriggerRef.current?.focus({ preventScroll: true });
  }, [visibleFilterIDs]);

  useEffect(() => {
    setVisibleFilterIDs((current) => {
      const next = new Set(current);
      for (const id of externallyActiveFilterIDs) next.add(id);
      return next.size === current.size ? current : next;
    });
    // filter object identity may change on every feature render; values drive visibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterSignature]);

  const visibleFilters = filters.filter((filter) => visibleFilterIDs.has(filterID(filter)));
  const availableFilters = filters.filter((filter) => !visibleFilterIDs.has(filterID(filter)));
  const editingFilter = filters.find((filter) => filterID(filter) === editingFilterID);
  const resolveFilterMenuAnchor = useCallback(() => (
    filterMenuStage === 'value' && editingFilterID
      ? filterChipAnchors.current.get(editingFilterID) ?? filterMenuAnchor ?? null
      : filterMenuAnchor ?? null
  ), [editingFilterID, filterMenuAnchor, filterMenuStage]);

  const closeFilterMenu = () => {
    setFilterMenuAnchor(undefined);
    setEditingFilterID(undefined);
    setFilterMenuStage('filter');
  };

  const openFilter = (event: MouseEvent<HTMLElement>, id: string) => {
    setEditingFilterID(id);
    setFilterMenuStage('value');
    setFilterMenuAnchor(event.currentTarget);
  };

  if (props.searchable === false && !filters.length && actions == null && trailing == null) return null;

  return (
    <Stack
      role={props.searchable === false ? undefined : 'search'}
      aria-label={props.searchable === false ? undefined : props.searchLabel ?? '목록 검색'}
      direction="row"
      spacing={1}
      useFlexGap
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {props.searchable !== false && <SearchField
        value={props.query}
        onValueChange={props.onQueryChange}
        label={props.searchLabel ?? '목록 검색'}
        placeholder={props.searchPlaceholder}
        sx={{ width: { xs: '100%', sm: designTokens.layout.filterSearchWidth }, flexShrink: 0 }}
      />}
      {visibleFilters.map((filter) => {
        const active = filter.value !== (filter.defaultValue ?? '');
        const selected = filter.kind === 'date'
          ? filter.value || '날짜 선택'
          : filter.options?.find((option) => option.value === filter.value)?.label ?? '전체';
        return <Chip
          key={filterID(filter)}
          ref={(element) => {
            if (element) filterChipAnchors.current.set(filterID(filter), element);
            else filterChipAnchors.current.delete(filterID(filter));
          }}
          label={`${filter.label}: ${selected}`}
          clickable
          variant="outlined"
          onClick={(event) => openFilter(event, filterID(filter))}
          onDelete={() => {
            // 삭제된 chip 대신 남아 있는 필터 버튼에서 키보드 작업을 이어갑니다.
            restoreFilterFocus.current = true;
            resetFilter(filter);
            setVisibleFilterIDs((current) => {
              const next = new Set(current);
              next.delete(filterID(filter));
              return next;
            });
          }}
          sx={{
            ...controlChipSx,
            maxWidth: { xs: '100%', sm: 240 },
            bgcolor: active ? designTokens.color.actionSoft : designTokens.color.canvas,
            borderColor: active ? designTokens.color.brandBorder : designTokens.color.divider,
            color: active ? designTokens.color.action : designTokens.color.secondaryText,
            '&.MuiChip-clickable:hover': { bgcolor: active ? designTokens.color.compactSelectedSurface : designTokens.color.hoverSurface },
            '&.Mui-focusVisible': { bgcolor: active ? designTokens.color.actionSoft : designTokens.color.canvas },
            '@media (hover: none)': { '&.MuiChip-clickable:hover': { bgcolor: active ? designTokens.color.actionSoft : designTokens.color.canvas } },
            '& .MuiChip-label': { p: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
            '& .MuiChip-deleteIcon': { ...controlChipSx['& .MuiChip-deleteIcon'], color: 'inherit' },
          }}
        />;
      })}
      {filters.length > 0 && <Chip
        ref={filterTriggerRef}
        label="필터"
        icon={<AddOutlined />}
        clickable
        variant="outlined"
        onClick={(event) => {
          setEditingFilterID(undefined);
          setFilterMenuStage('filter');
          setFilterMenuAnchor(event.currentTarget);
        }}
        disabled={availableFilters.length === 0}
        sx={{
          ...controlChipSx,
          bgcolor: designTokens.color.canvas,
          borderColor: designTokens.color.divider,
          color: designTokens.color.secondaryText,
        }}
      />}
      {actions}
      <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
      {trailing}

      <Menu
        anchorEl={resolveFilterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={closeFilterMenu}
        slotProps={{ list: { 'aria-label': filterMenuStage === 'value' && editingFilter ? `${editingFilter.label} 필터 값` : '추가할 필터 선택' } }}
      >
        {filterMenuStage === 'filter' ? availableFilters.map((filter) => <MenuItem key={filterID(filter)} onClick={() => {
          setVisibleFilterIDs((current) => new Set(current).add(filterID(filter)));
          setEditingFilterID(filterID(filter));
          setFilterMenuStage('value');
        }}>{filter.label}</MenuItem>) : editingFilter?.kind === 'date' ? <Box sx={{ width: 260, p: 1.5 }}>
          <FormTextField
            autoFocus
            fullWidth
            type="date"
            label={editingFilter.label}
            value={editingFilter.value}
            onChange={(event) => editingFilter.onChange(event.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1 }}>
            <Button size="small" onClick={() => { resetFilter(editingFilter); closeFilterMenu(); }}>지우기</Button>
            <Button size="small" variant="contained" onClick={closeFilterMenu}>적용</Button>
          </Stack>
        </Box> : editingFilter?.options?.map((option) => <MenuItem
          key={option.value}
          autoFocus={option.value === editingFilter.value}
          selected={option.value === editingFilter.value}
          onClick={() => {
            editingFilter.onChange(option.value);
            closeFilterMenu();
          }}
        >{option.label}</MenuItem>)}
      </Menu>
    </Stack>
  );
}
